import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  X, Buildings, Warehouse, UserCircle, Plus, PencilSimple, TrashSimple,
  CaretDown, CaretRight, MagnifyingGlass,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PartiesManager — Operational accountability registry UI.
 * Organization (pharma/hospital/distributor/carrier/warehouse) → Facility → Contact.
 * Strict operational / compliance / chain-of-custody framing. NOT a CRM.
 */
export default function PartiesManager({ open, onClose }) {
  const [tree, setTree] = useState([]);
  const [totals, setTotals] = useState({ organizations: 0, facilities: 0, contacts: 0 });
  const [enums, setEnums] = useState(null);
  const [expanded, setExpanded] = useState({}); // {orgId: true, facId: true}
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // {type, data?}
  const [loading, setLoading] = useState(false);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const [t, e] = await Promise.all([
        axios.get(`${API}/parties/tree`),
        enums ? Promise.resolve({ data: { ...enums } }) : axios.get(`${API}/parties/meta/enums`),
      ]);
      setTree(t.data.tree || []);
      setTotals(t.data.totals || {});
      if (!enums) setEnums(e.data);
    } catch (err) {
      toast.error("Failed to load parties");
    } finally {
      setLoading(false);
    }
  }, [enums]);

  useEffect(() => {
    if (open) loadTree();
  }, [open, loadTree]);

  if (!open) return null;

  const filtered = !search
    ? tree
    : tree
        .map((o) => {
          const orgMatch = o.name.toLowerCase().includes(search.toLowerCase());
          const facilities = (o.facilities || [])
            .map((f) => {
              const facMatch = f.name.toLowerCase().includes(search.toLowerCase());
              const contacts = (f.contacts || []).filter((c) =>
                c.full_name.toLowerCase().includes(search.toLowerCase())
              );
              if (facMatch || contacts.length) return { ...f, contacts: facMatch ? f.contacts : contacts };
              return null;
            })
            .filter(Boolean);
          const direct = (o.direct_contacts || []).filter((c) =>
            c.full_name.toLowerCase().includes(search.toLowerCase())
          );
          if (orgMatch || facilities.length || direct.length) {
            return { ...o, facilities: orgMatch ? o.facilities : facilities, direct_contacts: orgMatch ? o.direct_contacts : direct };
          }
          return null;
        })
        .filter(Boolean);

  return (
    <div className="fixed inset-0 z-[1900] bg-black/70 backdrop-blur-sm flex items-stretch justify-center" onClick={onClose}>
      <div className="bg-zinc-950 border-l border-zinc-800 w-full max-w-5xl ml-auto flex flex-col" onClick={(e) => e.stopPropagation()}
        data-testid="parties-manager">
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
              <Buildings size={18} className="text-teal-400" weight="duotone" />
              Operational Parties
            </h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Chain-of-custody stakeholders · Organization → Facility → Contact
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="parties-close">
            <X size={16} />
          </Button>
        </div>

        {/* Stats + search + create */}
        <div className="px-6 py-3 border-b border-zinc-800 bg-zinc-900/30 flex items-center gap-4">
          <div className="flex items-center gap-4 text-[11px] text-zinc-400">
            <span><span className="text-teal-300 font-mono font-semibold">{totals.organizations}</span> orgs</span>
            <span><span className="text-teal-300 font-mono font-semibold">{totals.facilities}</span> facilities</span>
            <span><span className="text-teal-300 font-mono font-semibold">{totals.contacts}</span> contacts</span>
          </div>
          <div className="flex-1 relative">
            <MagnifyingGlass size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search organizations, facilities, contacts…"
              className="bg-zinc-900 border-zinc-800 text-sm h-8 pl-8"
              data-testid="parties-search" />
          </div>
          <Button onClick={() => setEditing({ type: "organization", data: null })}
            className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8 font-semibold"
            data-testid="parties-new-org">
            <Plus size={12} className="mr-1" /> New Organization
          </Button>
        </div>

        {/* Tree */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && tree.length === 0 ? (
            <p className="text-xs text-zinc-500 text-center mt-10">Loading…</p>
          ) : filtered.length === 0 ? (
            <EmptyState onCreate={() => setEditing({ type: "organization", data: null })} />
          ) : (
            <div className="space-y-2">
              {filtered.map((org) => (
                <OrgNode
                  key={org.id}
                  org={org}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  onEditOrg={(o) => setEditing({ type: "organization", data: o })}
                  onAddFacility={(o) => setEditing({ type: "facility", data: { organization_id: o.id, _orgName: o.name } })}
                  onAddContact={(o) => setEditing({ type: "contact", data: { organization_id: o.id, _orgName: o.name } })}
                  onEditFacility={(f) => setEditing({ type: "facility", data: f })}
                  onAddContactToFac={(f) => setEditing({ type: "contact", data: { organization_id: f.organization_id, facility_id: f.id, _orgName: f.organization_name, _facName: f.name } })}
                  onEditContact={(c) => setEditing({ type: "contact", data: c })}
                  onDelete={async (kind, id, label) => {
                    if (!window.confirm(`Delete ${kind} "${label}"?`)) return;
                    try {
                      await axios.delete(`${API}/parties/${kind === "organization" ? "organizations" : kind === "facility" ? "facilities" : "contacts"}/${id}`);
                      toast.success(`${kind} deleted`);
                      loadTree();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || `Cannot delete ${kind}`);
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {editing && enums && (
          <PartyEditor
            editing={editing}
            enums={enums}
            tree={tree}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); loadTree(); }}
          />
        )}
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="text-center py-16">
      <Buildings size={32} className="mx-auto text-zinc-700 mb-2" weight="duotone" />
      <h3 className="text-sm font-semibold text-zinc-300 mb-1">No parties yet</h3>
      <p className="text-[11px] text-zinc-500 mb-4 max-w-md mx-auto">
        Build your chain-of-custody registry. Start with a pharma company, hospital, distributor,
        carrier, or warehouse — then attach facilities and contacts.
      </p>
      <Button onClick={onCreate} className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8 font-semibold">
        <Plus size={12} className="mr-1" /> Create first organization
      </Button>
    </div>
  );
}

function OrgNode({ org, expanded, setExpanded, onEditOrg, onAddFacility, onAddContact, onEditFacility, onAddContactToFac, onEditContact, onDelete }) {
  const isOpen = expanded[org.id] !== false;
  return (
    <Card className="bg-zinc-900/40 border-zinc-800">
      <div className="flex items-center gap-2 px-3 py-2 group">
        <button onClick={() => setExpanded({ ...expanded, [org.id]: !isOpen })} className="text-zinc-500 hover:text-zinc-300"
          data-testid={`org-toggle-${org.id}`}>
          {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
        </button>
        <Buildings size={14} className="text-teal-400" weight="duotone" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-100 truncate">{org.name}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{org.type?.replace(/_/g, " ")}</div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" onClick={() => onAddFacility(org)} className="h-7 px-2 text-[10px] text-zinc-400 hover:text-zinc-100"
            data-testid={`add-facility-${org.id}`}>
            <Plus size={10} className="mr-0.5" /> Facility
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onAddContact(org)} className="h-7 px-2 text-[10px] text-zinc-400 hover:text-zinc-100">
            <Plus size={10} className="mr-0.5" /> Contact
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEditOrg(org)} className="h-7 w-7 p-0 text-zinc-400 hover:text-teal-300">
            <PencilSimple size={10} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete("organization", org.id, org.name)}
            className="h-7 w-7 p-0 text-zinc-400 hover:text-red-300">
            <TrashSimple size={10} />
          </Button>
        </div>
      </div>
      {isOpen && (
        <div className="pl-6 pr-3 pb-3 space-y-1">
          {(org.direct_contacts || []).map((c) => (
            <ContactRow key={c.id} contact={c} onEdit={onEditContact} onDelete={onDelete} />
          ))}
          {(org.facilities || []).map((f) => (
            <FacilityNode key={f.id} fac={f} onEdit={onEditFacility} onAddContact={onAddContactToFac} onEditContact={onEditContact} onDelete={onDelete} />
          ))}
          {(org.facilities || []).length === 0 && (org.direct_contacts || []).length === 0 && (
            <p className="text-[10px] text-zinc-600 italic pl-4 py-1">No facilities or contacts yet</p>
          )}
        </div>
      )}
    </Card>
  );
}

function FacilityNode({ fac, onEdit, onAddContact, onEditContact, onDelete }) {
  return (
    <div className="rounded border border-zinc-800 bg-zinc-950/30 group">
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        <Warehouse size={12} className="text-amber-400" weight="duotone" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-zinc-200 truncate">{fac.name}</div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">
            {fac.type?.replace(/_/g, " ")}{fac.address ? ` · ${fac.address}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          <Button variant="ghost" size="sm" onClick={() => onAddContact(fac)} className="h-6 px-1.5 text-[10px] text-zinc-400 hover:text-zinc-100">
            <Plus size={9} className="mr-0.5" /> Contact
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(fac)} className="h-6 w-6 p-0 text-zinc-400 hover:text-teal-300">
            <PencilSimple size={9} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete("facility", fac.id, fac.name)}
            className="h-6 w-6 p-0 text-zinc-400 hover:text-red-300">
            <TrashSimple size={9} />
          </Button>
        </div>
      </div>
      {(fac.contacts || []).length > 0 && (
        <div className="pl-5 pr-2 pb-1.5 space-y-0.5">
          {fac.contacts.map((c) => (
            <ContactRow key={c.id} contact={c} onEdit={onEditContact} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

function ContactRow({ contact, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1 rounded hover:bg-zinc-800/40 group">
      <UserCircle size={11} className="text-zinc-500" weight="duotone" />
      <div className="flex-1 min-w-0 flex items-baseline gap-2">
        <span className="text-xs text-zinc-200 truncate">{contact.full_name}</span>
        <span className="text-[9px] uppercase tracking-wider text-zinc-500">{contact.role?.replace(/_/g, " ")}</span>
        {contact.escalation_level > 1 && (
          <span className="text-[9px] font-mono text-amber-400">L{contact.escalation_level}</span>
        )}
        {contact.email && <span className="text-[10px] font-mono text-zinc-600 truncate">{contact.email}</span>}
      </div>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
        <Button variant="ghost" size="sm" onClick={() => onEdit(contact)} className="h-6 w-6 p-0 text-zinc-500 hover:text-teal-300">
          <PencilSimple size={9} />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete("contact", contact.id, contact.full_name)}
          className="h-6 w-6 p-0 text-zinc-500 hover:text-red-300">
          <TrashSimple size={9} />
        </Button>
      </div>
    </div>
  );
}

function PartyEditor({ editing, enums, tree, onClose, onSaved }) {
  const { type, data } = editing;
  const isEdit = !!data?.id;
  const [form, setForm] = useState(() => {
    // Build defaults first, then overlay `data` (for Edit OR partial seed in Add).
    // Add-mode passes a partial seed like { organization_id, _orgName } which MUST NOT
    // overwrite the required defaults (type, name, etc.) — otherwise the POST 422s.
    const defaults =
      type === "organization" ? { name: "", type: "pharma", notes: "" } :
      type === "facility"     ? {
        organization_id: "", name: "", type: "warehouse",
        address: "", lat: null, lng: null, radius_m: 250,
        contact_name: "", contact_phone: "", hours: "", dock_count: "",
        notes: "", create_geofence: true,
      } :
      type === "contact"      ? { organization_id: "", facility_id: "", full_name: "", role: "qa_manager", email: "", phone: "", title: "", escalation_level: 1, notes: "" } :
      {};
    return { ...defaults, ...(data || {}) };
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      // Strip UI-only helper fields (prefixed with `_`) and Mongo id (`_id`)
      // before posting so the backend Pydantic model never sees junk.
      let payload = Object.fromEntries(
        Object.entries(form).filter(([k]) => !k.startsWith("_"))
      );
      // Coerce facility numerics so the backend Pydantic model accepts them.
      if (type === "facility") {
        // Geofence creation is opt-in (default true). When the toggle is OFF
        // OR lat/lng are missing, we send create_geofence=false so the backend
        // skips the geofence side-effect and just persists the facility record.
        const wantsGeofence = payload.create_geofence !== false;
        const hasCoords     = payload.lat != null && payload.lng != null
                              && payload.lat !== "" && payload.lng !== "";
        if (wantsGeofence && !hasCoords) {
          toast.error("To attach a geofence we need coordinates — pick from autocomplete, drop manual lat/lng, or turn off 'Create geofence'.");
          setBusy(false);
          return;
        }
        payload.create_geofence = wantsGeofence && hasCoords;
        payload.lat = hasCoords ? parseFloat(payload.lat) : null;
        payload.lng = hasCoords ? parseFloat(payload.lng) : null;
        payload.radius_m = parseFloat(payload.radius_m) || 250;
        payload.dock_count = payload.dock_count ? parseInt(payload.dock_count, 10) : null;
        // Empty strings → null so they don't fail enum validation
        ["address","contact_name","contact_phone","hours","notes"].forEach((k) => {
          if (payload[k] === "") payload[k] = null;
        });
      }
      const url = `${API}/parties/${type === "organization" ? "organizations" : type === "facility" ? "facilities" : "contacts"}${isEdit ? `/${data.id}` : ""}`;
      if (isEdit) await axios.put(url, payload);
      else await axios.post(url, payload);
      toast.success(`${type} ${isEdit ? "updated" : "created"}${type === "facility" ? " · geofence linked" : ""}`);
      onSaved();
    } catch (e) {
      // Pydantic validation errors return `detail` as an array of objects.
      // Coerce to a friendly string so we never crash the React tree with
      // "Objects are not valid as a React child".
      const detail = e.response?.data?.detail;
      let msg = "Save failed";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail
          .map((d) => {
            const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
            return loc ? `${loc}: ${d.msg}` : (d.msg || JSON.stringify(d));
          })
          .join(" · ");
      } else if (detail && typeof detail === "object") {
        msg = detail.msg || JSON.stringify(detail);
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const facilitiesForOrg = tree.find((o) => o.id === form.organization_id)?.facilities || [];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1950]" onClick={onClose}>
      <Card className="bg-zinc-950 border-zinc-800 p-6 w-[520px] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
        data-testid="party-editor">
        <h3 className="text-sm font-semibold text-zinc-100 mb-1 capitalize">
          {isEdit ? "Edit" : "New"} {type.replace(/_/g, " ")}
        </h3>
        <p className="text-[11px] text-zinc-500 mb-4">
          {type === "organization" && "Pharma, hospital, distributor, carrier, warehouse company, etc."}
          {type === "facility" && (form._orgName ? `Under ${form._orgName}` : "Warehouse, lab, clinic, airport hub, distribution center, etc.")}
          {type === "contact" && (form._facName ? `Under ${form._orgName} → ${form._facName}` : form._orgName ? `Under ${form._orgName}` : "QA manager, receiver, compliance officer, dispatcher, etc.")}
        </p>

        <div className="space-y-3">
          {type === "contact" && (
            <Field label="Full Name *">
              <Input value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-sm h-8" data-testid="contact-name-input" />
            </Field>
          )}
          {(type === "organization" || type === "facility") && (
            <Field label="Name *">
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-sm h-8" data-testid={`${type}-name-input`} />
            </Field>
          )}

          {type === "organization" && (
            <Field label="Type">
              <Select value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={enums.organization_types} />
            </Field>
          )}
          {type === "facility" && (
            <FacilityFormFields form={form} setForm={setForm} tree={tree} enums={enums} />
          )}
          {type === "contact" && (
            <>
              <Field label="Role">
                <Select value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={enums.contact_roles} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Organization">
                  <Select value={form.organization_id || ""}
                    onChange={(v) => setForm({ ...form, organization_id: v, facility_id: "" })}
                    options={[{ value: "", label: "—" }, ...tree.map((o) => ({ value: o.id, label: o.name }))]} />
                </Field>
                <Field label="Facility">
                  <Select value={form.facility_id || ""}
                    onChange={(v) => setForm({ ...form, facility_id: v })}
                    options={[{ value: "", label: "—" }, ...facilitiesForOrg.map((f) => ({ value: f.id, label: f.name }))]}
                    disabled={!form.organization_id} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email">
                  <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 text-sm h-8" data-testid="contact-email-input" />
                </Field>
                <Field label="Phone">
                  <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 text-sm h-8" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Title">
                  <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 text-sm h-8" placeholder="e.g. Sr. QA Lead" />
                </Field>
                <Field label="Escalation Level (1-5)">
                  <Input type="number" min={1} max={5} value={form.escalation_level || 1}
                    onChange={(e) => setForm({ ...form, escalation_level: parseInt(e.target.value) || 1 })}
                    className="bg-zinc-900 border-zinc-800 text-sm h-8 font-mono" />
                </Field>
              </div>
            </>
          )}

          <Field label="Notes (optional)">
            <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} maxLength={500}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-xs h-8">Cancel</Button>
          <Button onClick={save} disabled={busy}
            className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8 font-semibold"
            data-testid="party-save-btn">
            {isEdit ? "Save Changes" : "Create"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

const TEMPLATE_RADIUS = {
  warehouse: 250, distribution_center: 500, store: 100, port: 1000,
  manufacturing: 750, crossdock: 250, yard: 250,
  customer_site: 250, supplier_site: 250,
  lab: 150, clinic: 100, airport_hub: 500, cold_storage: 300,
  office: 100, other: 250,
};

/**
 * Facility editor — address autocomplete (Nominatim → geocoded lat/lng) →
 * auto-creates a circular geofence at save time. Operators can adjust the
 * radius inline before saving. No map drawing required.
 */
function FacilityFormFields({ form, setForm, tree, enums }) {
  const [addrQuery, setAddrQuery] = useState(form.address || "");
  const [addrResults, setAddrResults] = useState([]);
  const [addrFocused, setAddrFocused] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const skipNextRef = useRef(false);

  useEffect(() => {
    if (skipNextRef.current) { skipNextRef.current = false; return; }
    if (!addrQuery || addrQuery.length < 3) { setAddrResults([]); return; }
    const t = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&q=${encodeURIComponent(addrQuery)}`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        setAddrResults(await res.json() || []);
      } catch { setAddrResults([]); }
      finally { setAddrLoading(false); }
    }, 450);
    return () => clearTimeout(t);
  }, [addrQuery]);

  const pick = (r) => {
    const lat = parseFloat(r.lat), lng = parseFloat(r.lon);
    if (isNaN(lat) || isNaN(lng)) return;
    skipNextRef.current = true;
    setAddrQuery(r.display_name);
    setAddrResults([]);
    setAddrFocused(false);
    setForm({ ...form, address: r.display_name, lat, lng });
  };

  return (
    <>
      <Field label="Parent Organization *">
        <Select value={form.organization_id}
          onChange={(v) => setForm({ ...form, organization_id: v })}
          options={tree.map((o) => ({ value: o.id, label: o.name }))}
          data-testid="facility-parent-org" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Facility Name *">
          <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Memphis Distribution Center"
            className="bg-zinc-900 border-zinc-800 text-sm h-8"
            data-testid="facility-name" />
        </Field>
        <Field label="Type">
          <Select value={form.type}
            onChange={(v) => {
              // Auto-update default radius when type changes, unless the user already touched it.
              setForm((prev) => ({
                ...prev, type: v,
                radius_m: prev._radiusTouched ? prev.radius_m : (TEMPLATE_RADIUS[v] || 250),
              }));
            }}
            options={enums.facility_types}
            data-testid="facility-type" />
        </Field>
      </div>

      <Field label="Address *">
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={addrQuery} onChange={(e) => setAddrQuery(e.target.value)}
            onFocus={() => setAddrFocused(true)}
            onBlur={() => setTimeout(() => setAddrFocused(false), 180)}
            placeholder="Start typing — e.g. '350 5th Ave, New York'"
            className="pl-7 bg-zinc-900 border-zinc-800 text-sm h-8"
            data-testid="facility-address" />
          {addrFocused && (addrResults.length > 0 || addrLoading) && (
            <div className="absolute top-full mt-1 left-0 right-0 z-[10] bg-zinc-950/98 border border-zinc-800 backdrop-blur-md rounded-sm overflow-hidden max-h-56 overflow-y-auto"
              data-testid="facility-address-results">
              {addrLoading && <div className="px-3 py-2 text-[10px] text-zinc-500 font-mono">Searching…</div>}
              {addrResults.map((r) => (
                <button key={r.place_id} type="button"
                  onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                  data-testid={`facility-address-result-${r.place_id}`}
                  className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-900 border-b border-zinc-900 last:border-b-0">
                  <div className="font-medium text-zinc-200 truncate">{r.name || r.display_name.split(",")[0]}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{r.display_name}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        {form.lat != null && form.lng != null && form.lat !== "" && form.lng !== "" ? (
          <div className="text-[10px] text-zinc-500 font-mono mt-1.5 flex items-center gap-3 flex-wrap"
            data-testid="facility-geocoded">
            <span className="text-emerald-400">● Coordinates locked</span>
            <span>{Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)}</span>
            <button type="button"
              onClick={() => setForm({ ...form, lat: null, lng: null })}
              className="text-zinc-600 hover:text-zinc-300 underline"
              data-testid="facility-clear-coords">
              clear
            </button>
          </div>
        ) : (
          <div className="text-[10px] text-zinc-500 mt-1.5">
            <span className="text-amber-400/80 italic">
              No autocomplete match? Drop the coordinates manually below — the address you typed will be saved as-is.
            </span>
          </div>
        )}
      </Field>

      {/* Manual coordinate entry — visible when address didn't geocode. Lets
          operators force-add a facility whose address isn't in OSM (rural
          plants, private terminals, brand-new builds). */}
      <Field label="Manual Coordinates (only needed if address didn't autocomplete)">
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" step="any" min="-90" max="90"
            placeholder="Latitude (e.g. 41.8781)"
            value={form.lat == null ? "" : form.lat}
            onChange={(e) => setForm({ ...form, lat: e.target.value === "" ? null : parseFloat(e.target.value) })}
            className="bg-zinc-900 border-zinc-800 text-sm h-8 font-mono"
            data-testid="facility-manual-lat" />
          <Input type="number" step="any" min="-180" max="180"
            placeholder="Longitude (e.g. -87.6298)"
            value={form.lng == null ? "" : form.lng}
            onChange={(e) => setForm({ ...form, lng: e.target.value === "" ? null : parseFloat(e.target.value) })}
            className="bg-zinc-900 border-zinc-800 text-sm h-8 font-mono"
            data-testid="facility-manual-lng" />
        </div>
        <div className="text-[10px] text-zinc-500 mt-1">
          Tip: right-click on Google Maps → first row is "lat, lng".
        </div>
      </Field>

      {/* Geofence is OPT-IN. Off by default once the operator explicitly
          unchecks it; on otherwise. When off the facility saves without
          arrival/departure events — useful for virtual sites or pending GPS. */}
      <Field label="">
        <label className="flex items-start gap-2 cursor-pointer p-2 rounded-sm bg-zinc-900/40 border border-zinc-800 hover:border-teal-500/40 transition-colors">
          <input type="checkbox"
            checked={form.create_geofence !== false}
            onChange={(e) => setForm({ ...form, create_geofence: e.target.checked })}
            className="mt-0.5 accent-teal-500"
            data-testid="facility-create-geofence" />
          <span className="text-xs text-zinc-200">
            Create a geofence at this facility
            <div className="text-[10px] text-zinc-500 mt-0.5">
              Required for arrival / departure / dwell-time events. Turn off
              for facility records without GPS (virtual sites, pending coords).
            </div>
          </span>
        </label>
      </Field>

      {form.create_geofence !== false && (
        <Field label={`Geofence Radius — ${form.radius_m || 250} m`}>
          <input type="range" min="50" max="5000" step="25"
            value={form.radius_m || 250}
            onChange={(e) => setForm({ ...form, radius_m: parseInt(e.target.value, 10), _radiusTouched: true })}
            className="w-full accent-teal-500"
            data-testid="facility-radius-slider" />
          <div className="flex items-center gap-2 mt-1">
            <Input type="number" min="50" step="10"
              value={form.radius_m || 250}
              onChange={(e) => setForm({ ...form, radius_m: parseInt(e.target.value, 10) || 250, _radiusTouched: true })}
              className="bg-zinc-900 border-zinc-800 text-sm h-8 font-mono w-28"
              data-testid="facility-radius" />
            <span className="text-[10px] text-zinc-500">m · auto-sized to {form.type.replace(/_/g, " ")} default ({TEMPLATE_RADIUS[form.type] || 250} m)</span>
          </div>
        </Field>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Contact Name">
          <Input value={form.contact_name || ""} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
            placeholder="Site lead, dock manager…"
            className="bg-zinc-900 border-zinc-800 text-sm h-8"
            data-testid="facility-contact-name" />
        </Field>
        <Field label="Contact Phone">
          <Input value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            placeholder="+1 901-555-1234"
            className="bg-zinc-900 border-zinc-800 text-sm h-8 font-mono"
            data-testid="facility-contact-phone" />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Hours">
          <Input value={form.hours || ""} onChange={(e) => setForm({ ...form, hours: e.target.value })}
            placeholder="Mon–Fri 6am–10pm"
            className="bg-zinc-900 border-zinc-800 text-sm h-8"
            data-testid="facility-hours" />
        </Field>
        <Field label="Dock Count">
          <Input type="number" min="0" value={form.dock_count || ""}
            onChange={(e) => setForm({ ...form, dock_count: e.target.value })}
            className="bg-zinc-900 border-zinc-800 text-sm h-8 font-mono"
            data-testid="facility-dock-count" />
        </Field>
      </div>

      <div className="text-[10px] text-zinc-500 italic border-t border-zinc-900 pt-2 mt-2">
        {form.create_geofence !== false && form.lat != null && form.lng != null ? (
          <>Saving will <span className="text-teal-400">automatically create / update the backing geofence</span>{" "}
          ({form.radius_m || 250} m circular) and link it to this facility.</>
        ) : (
          <>Saving as a <span className="text-amber-300">facility record only</span> — no geofence will be created.{" "}
          You can come back later and add coordinates to enable arrival / departure events.</>
        )}
      </div>
    </>
  );
}

function Select({ value, onChange, options, disabled, ...rest }) {
  const normalized = options.map((o) => (typeof o === "string" ? { value: o, label: o.replace(/_/g, " ") } : o));
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} {...rest}
      className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100 px-2.5 py-1.5 h-8 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:opacity-50">
      {normalized.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
